import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  DayPicker,
  type SelectSingleEventHandler,
  type DayPickerProps,
} from "react-day-picker";
import "react-day-picker/style.css";
import "./booking-calendar.css";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { sv } from "date-fns/locale/sv";
import { enUS } from "date-fns/locale/en-US";
import { nb } from "date-fns/locale/nb";
import { da } from "date-fns/locale/da";
import type { Locale } from "date-fns/locale";

const { useMemo } = React;

type DayAvailability = "available" | "partial" | "unavailable";

// Map language codes to date-fns locales
const localeMap: Record<string, Locale> = {
  sv: sv,
  en: enUS,
  no: nb,
  nb: nb,
  da: da,
};

export type CalendarProps = {
  mode?: "single";
  selected?: Date;
  getDateAvailability?: (date: Date) => DayAvailability | undefined;
  isLoadingAvailability?: boolean;
  onDateClick?: (date: Date) => void; // Custom click handler for clean API
  defaultMonth?: Date;
  startMonth?: Date;
  endMonth?: Date;
  lang?: string; // Language code for calendar localization (sv, en, no, da)
} & Omit<
  DayPickerProps,
  "mode" | "selected" | "onSelect" | "defaultMonth" | "startMonth" | "endMonth" | "locale"
>;

function BookingCalendar({
  className,
  classNames,
  showOutsideDays = true,
  getDateAvailability,
  isLoadingAvailability,
  onDateClick,
  selected,
  defaultMonth,
  startMonth,
  endMonth,
  lang,
  ...props
}: CalendarProps) {
  // Get the appropriate date-fns locale based on language code
  const calendarLocale = useMemo(() => {
    if (!lang) return enUS;
    const normalizedLang = lang.split("-")[0].toLowerCase();
    return localeMap[normalizedLang] || enUS;
  }, [lang]);
  // Validation: Warn if props are incorrectly set
  if (process.env.NODE_ENV === "development") {
    if (startMonth && selected) {
      const isStartMonthDynamic =
        Math.abs(startMonth.getTime() - selected.getTime()) < 86400000; // Within 1 day
      if (isStartMonthDynamic) {
        // Warning removed for production - startMonth dynamically set based on selected date
      }
    }
  }

  // Handle the required onSelect for v9 single mode
  const handleSelect: SelectSingleEventHandler = (date) => {
    if (!onDateClick) return;
    // DayPicker returns undefined when the user re-clicks the currently selected day.
    const effectiveDate = date ?? selected;
    if (effectiveDate) {
      onDateClick(effectiveDate);
    }
  };

  // Create modifiers for availability states - memoized to recalculate when data loads
  // FIXED: Date range should be independent of selected date to prevent disappearing indicators
  const modifiers = useMemo(() => {
    if (!getDateAvailability) return {};

    const available: Date[] = [];
    const partial: Date[] = [];
    const unavailable: Date[] = [];

    // FIXED: Always calculate from today, never use selected date as range start
    // This ensures all dates in the valid range always have availability indicators
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const rangeStart = startMonth || today;
    const start = new Date(rangeStart);
    start.setHours(0, 0, 0, 0);

    // FIXED: Use endMonth if provided, otherwise default to 365 days from start
    const rangeEnd =
      endMonth || new Date(start.getTime() + 365 * 24 * 60 * 60 * 1000);
    const end = new Date(rangeEnd);
    end.setHours(0, 0, 0, 0);

    const cursor = new Date(start);
    while (cursor <= end) {
      const date = new Date(cursor);
      const availability = getDateAvailability(date);

      if (availability === "available") available.push(date);
      else if (availability === "partial") partial.push(date);
      else if (availability === "unavailable") unavailable.push(date);

      cursor.setDate(cursor.getDate() + 1);
    }

    return { available, partial, unavailable };
  }, [
    getDateAvailability,
    isLoadingAvailability,
    startMonth,
    endMonth,
    // FIXED: Removed 'selected' and 'defaultMonth' from dependencies
    // Selection should NOT trigger recalculation of availability data
  ]); // Recalculate only when availability data loads or date range changes

  return (
    <DayPicker
      mode="single"
      selected={selected}
      onSelect={handleSelect}
      showOutsideDays={showOutsideDays}
      weekStartsOn={1} // Monday-start calendar
      locale={calendarLocale}
      defaultMonth={defaultMonth || selected || startMonth}
      startMonth={startMonth}
      endMonth={endMonth}
      className={cn("booking-calendar", className)}
      modifiers={modifiers}
      navLayout="around" // This places nav buttons on either side of caption!
      modifiersClassNames={{
        selected: "", // Handled by CSS
        today: "", // Handled by CSS
        available: "day-available",
        partial: "day-partial",
        unavailable: "day-unavailable",
      }}
      classNames={{
        months: "", // Let base styles handle layout
        month: "",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "", // Let base styles handle positioning
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "bg-transparent p-1 h-8 w-8 text-purple-900 hover:text-purple-700 hover:bg-purple-50",
        ),
        nav_button_previous: "",
        nav_button_next: "",
        table: "w-full border-spacing-0",
        head_row: "",
        head_cell: "text-muted-foreground font-normal text-[0.8rem]",
        row: "mt-1",
        cell: "text-center text-sm p-0 relative",
        // All day styles handled in DayComponent
        day: "",
        day_range_end: "",
        day_selected: "",
        day_today: "",
        day_outside: "opacity-50",
        day_disabled: "",
        day_range_middle: "",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, ...props }) => {
          if (orientation === "left") {
            return <ChevronLeft className="h-4 w-4 text-purple-900" />;
          }
          return <ChevronRight className="h-4 w-4 text-purple-900" />;
        },
      }}
      {...props}
    />
  );
}

export { BookingCalendar };
